import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useAppDispatch, useAppSelector } from '../../../../lib';
import { useGetListOfAnnotatorsQuery } from '../../api/metricsApiSlice';
import { getAllAnnotatorsOption } from '../../constants';
import { setSelectedAnnotatorId } from '../../state/metricsSlice';
import { selectSelectedAnnotatorId, selectSelectedProjectId } from '../../state/selector';
import { MetricAnnotatorUser } from '../../types/responseTypes';

interface useAnnotatorSelectionResults {
  annotatorOptions: MetricAnnotatorUser[];
  allAnnotatorOption: MetricAnnotatorUser;
  selectedAnnotatorId: string | null;
  handleAnnotatorChange: (e: React.SyntheticEvent, selectedId: string) => void;
  isAnnotatorDataLoading: boolean;
  selectedProjectId: string | null;
}

export const useAnnotatorSelection = (): useAnnotatorSelectionResults => {
  const { formatMessage } = useIntl();
  const allAnnotatorOption = getAllAnnotatorsOption(formatMessage);

  const selectedAnnotatorId = useAppSelector(selectSelectedAnnotatorId);
  const selectedProjectId = useAppSelector(selectSelectedProjectId);

  const dispatch = useAppDispatch();

  const {
    data: annotatorsData,
    isLoading: isAnnotatorsDataLoading,
    isFetching: isAnnotatorsDataFetching,
  } = useGetListOfAnnotatorsQuery(
    { projectId: selectedProjectId ?? '' },
    { skip: !selectedProjectId }
  );

  const annotatorOptions = useMemo(() => {
    if (!annotatorsData?.data) return [allAnnotatorOption];

    return [allAnnotatorOption, ...(annotatorsData?.data ?? [])];
  }, [annotatorsData?.data]);

  const handleAnnotatorChange = (e: React.SyntheticEvent, selectedId: string) => {
    e.preventDefault();
    dispatch(setSelectedAnnotatorId(selectedId));
  };

  return {
    annotatorOptions,
    allAnnotatorOption,
    selectedAnnotatorId,
    handleAnnotatorChange,
    isAnnotatorDataLoading: isAnnotatorsDataFetching || isAnnotatorsDataLoading,
    selectedProjectId,
  };
};
